/**
 * Aktuální akce areálu (husy, zabijačka, koncert…) — karty s fotkou, termínem
 * a tlačítkem. Data spravuje obsluha v appce (Admin → Web · ceny → Akce areálu);
 * skončené akce backend neposílá. Když není žádná akce, sekce se nevykreslí.
 *
 * Rezervace míst (Robin 24.–26. 10.): má-li akce kapacitu (míst na den), tlačítko
 * otevře formulář Den · Čas · Osoby · Jméno · Telefon. Server hlídá kapacitu;
 * jakmile je plno, karta se proškrtne: „Bohužel vyprodáno". Počet zbylých míst se
 * hostům NEukazuje — jen „poslední volná místa", když jich zbývá 10 a méně.
 * Čas se vybírá po půlhodině v okně, které nastaví admin (např. 12:00–18:00).
 * Bez kapacity vede tlačítko na odkaz (telefon, #kontakt…).
 */
import { useEffect, useState } from 'react'
import { apiUrl, assetUrl } from '../lib/api'

type Slot = { time: string; free: number }
type Day = { date: string; capacity: number; reserved: number; free: number; past: boolean; sold_out: boolean; peak?: number; slots?: Slot[] }
type Ev = {
  id: number; title: string; date_from: string; date_to: string | null; text: string
  image_url: string | null; cta_label: string; cta_href: string
  capacity?: number | null; times?: string; time_from?: string; time_to?: string; time_options?: string[]; days?: Day[]
}

const LAST_SEATS = 10   // „poslední volná místa" od 10 níž (Robin 26. 10.)

const M = ['ledna', 'února', 'března', 'dubna', 'května', 'června', 'července', 'srpna', 'září', 'října', 'listopadu', 'prosince']
function range(a: string, b: string | null): string {
  const [y1, m1, d1] = a.split('-').map(Number)
  if (!b || b === a) return `${d1}. ${M[m1 - 1]} ${y1}`
  const [y2, m2, d2] = b.split('-').map(Number)
  if (y1 === y2 && m1 === m2) return `${d1}.–${d2}. ${M[m1 - 1]} ${y1}`
  return `${d1}. ${M[m1 - 1]} – ${d2}. ${M[m2 - 1]} ${y2}`
}
const shortDate = (s: string) => { const [, m, d] = s.split('-').map(Number); return `${d}. ${m}.` }

/** Půlhodinová mřížka jako záloha, když admin nezadal okno ani časy. */
function halfHours(from = '12:00', to = '18:00'): string[] {
  const toMin = (t: string) => parseInt(t.slice(0, 2), 10) * 60 + parseInt(t.slice(3, 5), 10)
  const out: string[] = []
  for (let m = toMin(from); m <= toMin(to); m += 30) out.push(`${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`)
  return out
}

export function Events() {
  const [events, setEvents] = useState<Ev[]>([])
  const load = () => fetch(apiUrl('/api/sobacov/web/events'))
    .then((r) => (r.ok ? r.json() : null))
    .then((j) => { if (j?.events) setEvents(j.events) })
    .catch(() => {})
  useEffect(() => { load() }, [])
  if (!events.length) return null

  return (
    <section id="akce" className="py-10 md:py-14 scroll-mt-24">
      <div className="container-x">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="h-eyebrow">Aktuální akce</p>
            <h2 className="h-display text-3xl md:text-[40px] mt-3">Co se u nás chystá</h2>
          </div>
        </div>
        <div className={`mt-7 grid gap-5 ${events.length === 1 ? 'grid-cols-1' : events.length === 2 ? 'md:grid-cols-2' : 'md:grid-cols-2 lg:grid-cols-3'}`}>
          {events.map((e) => <EventCard key={e.id} e={e} single={events.length === 1} onChanged={load} />)}
        </div>
      </div>
    </section>
  )
}

function EventCard({ e, single, onChanged }: { e: Ev; single: boolean; onChanged: () => void }) {
  const days = e.days || []
  const reservable = (e.capacity || 0) > 0 && days.length > 0
  const openDays = days.filter((d) => !d.past && !d.sold_out)
  const soldOut = reservable && openDays.length === 0
  const lastSeats = reservable && !soldOut && openDays.every((d) => d.free <= LAST_SEATS)
  const [open, setOpen] = useState(false)
  const label = e.cta_label || 'Rezervovat místa'

  return (
    <article className={`card overflow-hidden flex ${single ? 'flex-col md:flex-row' : 'flex-col'} ${soldOut ? 'opacity-90' : ''}`}>
      {e.image_url && (
        /* Fotka celá, v původním poměru stran — žádný ořez (Robin 26. 10.) */
        <div className={`relative overflow-hidden bg-ink-900/5 ${single ? 'md:w-1/2 md:self-start' : ''}`}>
          <img src={assetUrl(e.image_url)} alt={e.title} className={`block w-full h-auto ${soldOut ? 'grayscale-[60%]' : ''}`} loading="lazy" />
          <span className="absolute top-3 left-3 bg-brand-500 text-white text-[11px] font-bold uppercase tracking-[0.14em] px-2.5 py-1 rounded-md shadow-md">
            {range(e.date_from, e.date_to)}
          </span>
          {soldOut && (
            <span className="absolute inset-x-0 bottom-0 bg-ink-900/85 text-white text-center text-[13px] font-bold uppercase tracking-[0.18em] py-2">
              Bohužel vyprodáno
            </span>
          )}
        </div>
      )}
      <div className="p-5 md:p-6 flex-1 flex flex-col">
        {!e.image_url && (
          <span className="self-start bg-brand-500 text-white text-[11px] font-bold uppercase tracking-[0.14em] px-2.5 py-1 rounded-md">
            {range(e.date_from, e.date_to)}
          </span>
        )}
        <h3 className={`mt-2 font-bold text-[22px] text-ink-900 tracking-tight leading-tight ${soldOut ? 'line-through decoration-brand-500 decoration-[3px] text-ink-700' : ''}`}>
          {e.title}
        </h3>
        {soldOut && <p className="mt-1 text-brand-600 font-bold text-[14px] uppercase tracking-[0.12em]">Bohužel vyprodáno</p>}
        {e.text && <p className="mt-2 text-[14px] text-ink-700 leading-relaxed whitespace-pre-line">{e.text}</p>}

        {/* Bez počtů míst: víc dní = stav každého dne; jeden den = jen upozornění na poslední místa */}
        {reservable && !soldOut && days.length > 1 && (
          <div className="mt-3 flex flex-wrap gap-1.5">
            {days.filter((d) => !d.past).map((d) => (
              <span
                key={d.date}
                className={`text-[11px] font-semibold px-2 py-1 rounded-md border ${d.sold_out ? 'border-ink-900/10 text-ink-700/50 line-through' : d.free <= LAST_SEATS ? 'border-amber-300 bg-amber-50 text-amber-800' : 'border-forest-200 bg-forest-50 text-forest-700'}`}
              >
                {shortDate(d.date)} · {d.sold_out ? 'vyprodáno' : d.free <= LAST_SEATS ? 'poslední volná místa' : 'volno'}
              </span>
            ))}
          </div>
        )}
        {reservable && !soldOut && days.length === 1 && lastSeats && (
          <p className="mt-3 text-[12px] font-bold uppercase tracking-[0.12em] text-amber-800">Poslední volná místa</p>
        )}

        {reservable ? (
          !soldOut && !open && (
            <button type="button" onClick={() => setOpen(true)} className="btn-book self-start mt-4 text-[13px] py-2.5 px-4">
              {label} <span aria-hidden="true">→</span>
            </button>
          )
        ) : (
          e.cta_label && e.cta_href && (
            <a href={e.cta_href} className="btn-book self-start mt-4 text-[13px] py-2.5 px-4">
              {e.cta_label} <span aria-hidden="true">→</span>
            </a>
          )
        )}

        {reservable && open && !soldOut && (
          <ReserveForm e={e} openDays={openDays} onDone={() => { onChanged() }} onClose={() => setOpen(false)} />
        )}
      </div>
    </article>
  )
}

function ReserveForm({ e, openDays, onDone, onClose }: { e: Ev; openDays: Day[]; onDone: () => void; onClose: () => void }) {
  const times = (e.time_options && e.time_options.length ? e.time_options : halfHours(e.time_from || '12:00', e.time_to || '18:00'))
  const [day, setDay] = useState(openDays[0]?.date || '')
  const [time, setTime] = useState(times[0] || '')
  const [persons, setPersons] = useState(2)
  const [name, setName] = useState('')
  const [phone, setPhone] = useState('')
  const [note, setNote] = useState('')
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  const [done, setDone] = useState<{ id: number } | null>(null)

  const cur = openDays.find((d) => d.date === day)
  // Kapacita platí pro každou chvíli zvlášť (sezení 2 h): volno se liší podle času příchodu
  const slotFree = (t: string) => cur?.slots?.find((s) => s.time === t)?.free
  const isFull = (t: string) => { const f = slotFree(t); return f !== undefined && f <= 0 }
  useEffect(() => {
    if (time && isFull(time)) { const first = times.find((t) => !isFull(t)); if (first) setTime(first) }
  }, [day]) // eslint-disable-line react-hooks/exhaustive-deps
  const maxPersons = Math.max(1, Math.min(20, slotFree(time) ?? cur?.free ?? 20))

  async function submit(ev: React.FormEvent) {
    ev.preventDefault()
    if (!day || !time || !name.trim() || !phone.trim()) { setErr('Vyplňte prosím den, čas, jméno a telefon.'); return }
    setBusy(true); setErr(null)
    try {
      const r = await fetch(apiUrl(`/api/sobacov/web/events/${e.id}/reserve`), {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ guest_name: name.trim(), guest_phone: phone.trim(), persons, day, time, note: note.trim() || null }),
      })
      const j = await r.json().catch(() => ({}))
      if (!r.ok || !j.ok) {
        const d = j?.detail
        setErr(typeof d === 'string' ? d : 'Rezervaci se nepodařilo uložit. Zavolejte prosím 775 140 705.')
        if (r.status === 409) onDone()
        return
      }
      setDone({ id: j.id })
      onDone()
    } catch {
      setErr('Spojení se nezdařilo. Zkuste to znovu nebo zavolejte 775 140 705.')
    } finally { setBusy(false) }
  }

  const inp = 'mt-1 w-full border border-ink-900/15 bg-white px-3 py-2 text-[14px] text-ink-900 focus:outline-none focus:border-brand-500'
  const lbl = 'block text-[10px] uppercase tracking-[0.16em] font-bold text-ink-700/70'

  if (done) {
    return (
      <div className="mt-4 bg-forest-50 border border-forest-200 p-4">
        <div className="font-bold text-forest-700 text-[15px]">✓ Rezervace přijata</div>
        <p className="mt-1 text-[13px] text-ink-800 leading-snug">
          {e.title} · {shortDate(day)} v {time} · {persons} {persons === 1 ? 'osoba' : persons < 5 ? 'osoby' : 'osob'}. Číslo rezervace <b>#{done.id}</b>, potvrzení posíláme SMS na {phone}.
        </p>
      </div>
    )
  }

  return (
    <form onSubmit={submit} className="mt-4 bg-paper border border-ink-900/[0.08] p-4 space-y-3">
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        <label className="block">
          <span className={lbl}>Den</span>
          <select value={day} onChange={(ev) => setDay(ev.target.value)} className={inp}>
            {openDays.map((d) => <option key={d.date} value={d.date}>{shortDate(d.date)}{d.free <= LAST_SEATS ? ' · poslední místa' : ''}</option>)}
          </select>
        </label>
        <label className="block">
          <span className={lbl}>Čas příchodu</span>
          {times.length > 0 ? (
            <select value={time} onChange={(ev) => setTime(ev.target.value)} className={inp}>
              {times.map((t) => {
                const f = slotFree(t); const full = isFull(t)
                return <option key={t} value={t} disabled={full}>{t}{full ? ' · plno' : f !== undefined && f <= LAST_SEATS ? ' · poslední místa' : ''}</option>
              })}
            </select>
          ) : (
            <input type="time" value={time} onChange={(ev) => setTime(ev.target.value)} step={1800} required className={inp} />
          )}
        </label>
        <label className="block">
          <span className={lbl}>Osob</span>
          <select value={Math.min(persons, maxPersons)} onChange={(ev) => setPersons(parseInt(ev.target.value, 10))} className={inp}>
            {Array.from({ length: maxPersons }, (_, i) => i + 1).map((n) => <option key={n} value={n}>{n}</option>)}
          </select>
        </label>
      </div>
      <div className="grid sm:grid-cols-2 gap-3">
        <label className="block">
          <span className={lbl}>Jméno a příjmení</span>
          <input value={name} onChange={(ev) => setName(ev.target.value)} required minLength={2} maxLength={120} autoComplete="name" className={inp} />
        </label>
        <label className="block">
          <span className={lbl}>Telefon</span>
          <input type="tel" value={phone} onChange={(ev) => setPhone(ev.target.value)} required minLength={6} maxLength={30} autoComplete="tel" placeholder="+420 …" className={inp} />
        </label>
      </div>
      <label className="block">
        <span className={lbl}>Poznámka (nepovinné)</span>
        <input value={note} onChange={(ev) => setNote(ev.target.value)} maxLength={300} placeholder="např. dětská židlička, oslava…" className={inp} />
      </label>
      {err && <div className="text-brand-600 text-[13px] font-semibold">{err}</div>}
      <div className="flex flex-wrap items-center gap-3">
        <button type="submit" disabled={busy} className="btn-book text-[13px] py-2.5 px-5 disabled:opacity-60">
          {busy ? 'Odesílám…' : 'Potvrdit rezervaci'}
        </button>
        <button type="button" onClick={onClose} className="text-[12px] uppercase tracking-[0.12em] font-bold text-ink-700 hover:text-brand-600">Zrušit</button>
        <span className="text-[11px] text-ink-700/60">Bez platby předem · potvrzení SMS</span>
      </div>
    </form>
  )
}
